import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { post_FamilyMember } from '../../services/FamilyMember';
import { FileDrop } from '../../components/FileDrop';
import Select from "react-select";
import { getMemberByGroupId } from '../../services/Member';
import UsernameValidatedComponent from '../../components/UsernameValidatedComponent';
import { getFamilyMemberUsername } from '../../services/UsernameChecking';
import EmailAlreadyExistChecking from '../../components/EmailAlreadyExistChecking';
import { getFamilyMemberEmailChecking } from '../../services/EmailChecking';
import baseURL from '../../config/api';
import useAuth from '../../hook/useAuth';
import { formatUSPhone, handleUSPhoneChange } from '../../utils/phoneFormatter';
import { useUSPhoneValidation } from '../../hook/useUSPhoneValidation';
import useConfirm from '../../hook/useConfirm';
import CustomFormatDatePicker from '../../components/CustomFormatDatePicker';
import useGroup from '../../hook/useGroup';
import useSubGroup from '../../hook/useSubGroup';
import Cookies from 'js-cookie';

const AddFamilyMembers = () => {
  const token = Cookies.get("token-my-cdir");
  const { auth }: any = useAuth();
  const { selectedGroupId }: any = useGroup();
  const { allSubGroups }: any = useSubGroup();
  const navigate = useNavigate();
  const { setConfirmPopup, setData }: any = useConfirm();
  const [memberData, setMemberData] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [subGroups, setSubGroups] = useState<any>(null);
  const [userData, setUserData] = useState<any>({
    groupId: 0,
    firstName: "",
    lastName: "",
    emailAddress: "",
    userName: "",
    phoneNumber: "",
    birthday: "",
    memberName: "",
    memberRoleId: 0,
    memberId: 0,
    isEnabled: true,
    isMinor: false,
    relationToMember: "",
    imageSize: 0,
    createdAt: "",
    createdBy: "",
    uploadedAt: "",
    uploadedBy: ""
  });
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  const [usernameTouched, setUsernameTouched] = useState<boolean>(false);

  const {
    isValid: isPhoneValid,
    touched: phoneTouched,
    setTouched: setPhoneTouched
  } = useUSPhoneValidation(userData.phoneNumber);
  const [isUsernameChecking, setIsUsernameChecking] = useState<boolean>(false);
  const [isEmailChecking, setIsEmailChecking] = useState<boolean>(false);

  useEffect(() => {
    fetch(`${baseURL}memberRoles`, {
      headers: {
        Authorization: `Bearer ${token?.toString()}`
      }
    })
      .then((res) => res.json())
      .then((data) => {
        const activeMembers = data?.filter((item: any) => item.isActive);
        setRoles(() => activeMembers?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.roleName
          }
        }));

        setUserData({
          ...userData,
          groupId: selectedGroupId,
          memberRoleId: activeMembers?.length > 0 ? activeMembers[activeMembers.length - 1]?.id : 0
        });
      })
      .catch(error => {
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedGroupId > 0) {
      setSubGroups("");
      getMemberByGroupId(selectedGroupId, token!.toString())
        .then(res => {
          setMemberData(res.map((item: any) => {
            return {
              id: item.id,
              name: item.familyName,
              groupId: item.groupId
            }
          }));
        })
        .catch(error => {
          console.log(error);
          setMemberData([]);
        });
    } else {
      setMemberData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId])

  const close = () => navigate(-1);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (userData.isMinor && (!userData.memberId || userData.memberId === 0)) {
      setData({
        title: "Validation Warning",
        summary: "Please select a Family member for a minor.",
        confirmBtn: "Ok",
        isConfirm: (callback: any) => {
          callback();
        },
      });

      setConfirmPopup(true);
      return;
    }

    const isLoginMissing = (!userData?.emailAddress);

    const proceedSubmit = () => {
      if (!isProcessing) {
        setIsProcessing(true);

        post_FamilyMember({
          ...userData,
          groupId: selectedGroupId,
          userName: userData.userName,
          password: "",
          memberId: userData?.memberId === 0 ? null : userData?.memberId,
          createdAt: new Date().toISOString(),
          createdBy: auth?.givenname,
          familyMemberSubGroups: (Array.isArray(subGroups) && subGroups?.length > 0)
            ? subGroups?.map((item: any) => ({ SubGroupId: item?.value }))
            : []
        }, token!.toString())
          .then(() => {
            setIsProcessing(false);
            navigate(-1);
          })
          .catch(error => {
            const errorMessage = error?.response?.data || error?.message || "Something went wrong, Please try again.";
            setData({
              title: 'Warning',
              summary: errorMessage,
              confirmBtn: 'Ok',
              isConfirm: (callback: any) => {
                callback();
              }
            });
            setConfirmPopup(true);
            setIsProcessing(false);
            console.error(error);
          });
      }
    };

    if (isLoginMissing) {
      setData({
        title: "Proceed without login details?",
        summary: "Without username and password this member can't login to the website or app.",
        isCancelBtn: true,
        confirmBtn: "Ok",
        isConfirm: (done: any) => {
          proceedSubmit();
          done();
        }
      });

      setConfirmPopup(true);
      return;
    }

    proceedSubmit();
  };

  return (
    <div className="w-full">
      <div className="my-2">
        <div className="py-8 px-6 relative bg-white">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <h2 className="text-lg font-bold text-secondary sm:text-xl">
              Add Member
            </h2>

            <label className="group relative inline-flex w-fit cursor-pointer items-center gap-2.5 rounded border border-secondary/20 bg-secondary/5 px-3.5 py-2.5 shadow-sm transition-all duration-200 hover:border-secondary/40 hover:bg-secondary/10">
              <input
                type="checkbox"
                checked={userData.isMinor}
                onChange={(e) =>
                  setUserData((prev: any) => ({
                    ...prev,
                    isMinor: e.target.checked
                  }))
                }
                className="h-4 w-4 shrink-0 cursor-pointer accent-secondary"
              />

              <span className="whitespace-nowrap text-sm font-medium text-secondary">
                Is Minor
              </span>

              <span className="relative flex shrink-0 items-center">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-secondary/10 text-xs font-bold text-secondary transition-colors group-hover:bg-secondary/20">
                  !
                </span>

                <span className="pointer-events-none absolute left-0 top-full z-50 mt-2 w-64 max-w-[calc(100vw-2rem)] translate-y-1 rounded border border-secondary/20 bg-white p-3 text-xs leading-relaxed text-gray-600 opacity-0 shadow-lg transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 sm:left-auto sm:right-0">
                  <span className="mb-1 block font-semibold text-secondary">
                    Minor Account Warning
                  </span>

                  Minor members cannot log in to the website or app.

                  <span className="absolute -top-1 left-2 h-2 w-2 rotate-45 border-l border-t border-secondary/20 bg-white sm:left-auto sm:right-2" />
                </span>
              </span>
            </label>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 grid xm:grid-cols-2 gap-6 text-secondary">
            <div>
              <label className="font-semibold text-sm">First Name</label>
              <input type='text' placeholder='Enter First Name'
                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                value={userData.firstName}
                onChange={(e) => setUserData({ ...userData, firstName: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="font-semibold text-sm">Last Name</label>
              <input type='text' placeholder='Enter Last Name'
                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                value={userData.lastName}
                onChange={(e) => setUserData({ ...userData, lastName: e.target.value })}
                required
              />
            </div>
            <UsernameValidatedComponent
              userName={userData.userName}
              setUserName={(value) => setUserData({ ...userData, userName: value })}
              getUsername={getFamilyMemberUsername}
              className={{
                parentDiv: "",
                label: "font-semibold text-sm",
                input: "w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary",
              }}
              touched={usernameTouched}
              setTouched={setUsernameTouched}
              required={false}
              setIsChecking={setIsUsernameChecking}
            />
            <EmailAlreadyExistChecking
              text={userData.emailAddress}
              setText={(value) => setUserData({ ...userData, emailAddress: value })}
              getEmailChecking={getFamilyMemberEmailChecking}
              className={{
                parentDiv: "",
                label: "font-semibold text-sm",
                input: "w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary",
              }}
              touched={emailTouched}
              setTouched={setEmailTouched}
              setIsChecking={setIsEmailChecking}
              required
              hideEmailCheck={userData?.isMinor}
            />
            <div>
              <label className="font-semibold text-sm">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formatUSPhone(userData.phoneNumber)}
                onChange={(e) => {
                  setPhoneTouched(true);
                  handleUSPhoneChange(e, (digits) =>
                    setUserData((prev: any) => ({
                      ...prev,
                      phoneNumber: digits,
                    }))
                  );
                }}
                className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
                placeholder="Phone No."
              />

              {!isPhoneValid && phoneTouched && (
                <p className="text-xs text-red-500 mt-1">
                  Please enter a valid 10-digit phone number
                </p>
              )}
            </div>

            <div>
              <label className="font-semibold text-sm">
                Birthday
              </label>

              <CustomFormatDatePicker
                value={userData.birthday}
                onChange={(val) =>
                  setUserData((prev: any) => ({
                    ...prev,
                    birthday: val,
                  }))
                }
                required={false}
              />
            </div>

            <div>
              <label className="font-semibold text-sm">Family</label>
              <select
                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                value={userData.memberId}
                onChange={(e) => setUserData({ ...userData, memberId: parseInt(e.target.value) })}
                required={userData?.isMinor}
              >
                <option value={0} disabled={userData.isMinor}>{"None of them"}</option>
                {memberData.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-semibold text-sm">Ministries</label>
              <Select
                isMulti
                className="basic-single mt-3"
                classNamePrefix="select"
                isDisabled={false}
                isLoading={false}
                isClearable={false}
                isRtl={false}
                isSearchable={false}
                name="subGroups"
                value={subGroups}
                onChange={(value) => setSubGroups(value)}
                options={allSubGroups}
              />
            </div>
            <div>
              <label className="font-semibold text-sm">Role</label>
              <select
                value={userData.memberRoleId || ""}
                onChange={(e) => {
                  setUserData({ ...userData, memberRoleId: parseInt(e.target.value) });
                }}
                onInvalid={(e: any) => e.target.setCustomValidity("Please select a role")}
                onInput={(e: any) => e.target.setCustomValidity("")}
                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
              >
                {roles.map((item: any, ind: number) => (
                  <option key={ind} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-semibold text-sm">Account Status</label>

              <div className="flex items-center gap-3 mt-4">
                <input
                  type="checkbox"
                  checked={userData.isEnabled}
                  onChange={(e) =>
                    setUserData((prev: any) => ({
                      ...prev,
                      isEnabled: e.target.checked
                    }))
                  }
                  className="w-4 h-4 accent-secondary cursor-pointer"
                />

                <span className="text-sm text-black">
                  {userData.isEnabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>

            <div>
              <FileDrop
                value={userData.filePath}
                setValue={(value) => setUserData({ ...userData, filePath: value })}
              />
            </div>

            <div />

            <div className="flex max-xm:flex-col gap-4 !mt-4">
              <button
                type="button"
                className="px-6 py-2 min-w-[150px] rounded text-black text-sm font-semibold border-none outline-none bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
                onClick={close}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 min-w-[150px] rounded text-white text-sm font-semibold border-none outline-none bg-secondary hover:bg-custom-blue"
                disabled={isUsernameChecking || isEmailChecking}
              >
                {
                  (isUsernameChecking || isEmailChecking || isProcessing)
                    ?
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18px" fill="#fff" className="mr-2 inline animate-spin"
                        viewBox="0 0 26.349 26.35">
                        <circle cx="13.792" cy="3.082" r="3.082" data-original="#000000" />
                        <circle cx="13.792" cy="24.501" r="1.849" data-original="#000000" />
                        <circle cx="6.219" cy="6.218" r="2.774" data-original="#000000" />
                        <circle cx="21.365" cy="21.363" r="1.541" data-original="#000000" />
                        <circle cx="3.082" cy="13.792" r="2.465" data-original="#000000" />
                        <circle cx="24.501" cy="13.791" r="1.232" data-original="#000000" />
                        <path
                          d="M4.694 19.84a2.155 2.155 0 0 0 0 3.05 2.155 2.155 0 0 0 3.05 0 2.155 2.155 0 0 0 0-3.05 2.146 2.146 0 0 0-3.05 0z"
                          data-original="#000000" />
                        <circle cx="21.364" cy="6.218" r=".924" data-original="#000000" />
                      </svg>
                      Processing...
                    </>
                    :
                    "Save"
                }
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>
  )
}

export default AddFamilyMembers;